import {
  addDoc,
  collection,
  deleteField,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, db, functions } from '@/core/firebase/client'
import { FirestoreRepository } from '@/core/firebase/repository'
import { ROOT_CLUBS, clubSubcollections } from '@/core/firebase/paths'
import { ClubSchema, type Club } from '@/core/models/club'
import { exemptLifecycleEffect, type BillingStatus, type ClubStatus } from '@/core/models/club-status'
import type { PlatformFeatures } from '@/core/models/platform-features'
import {
  MockupCleanupLogEntrySchema,
  type MockupCleanupLogEntry,
} from '@/core/models/mockup-cleanup-log'

const ROOT_MOCKUP_CLEANUP_LOG = 'mockup_cleanup_log'

/**
 * Append-only admin audit row at `clubs/{clubId}/data_operations/{auto-id}`.
 * Same collection the destructive CFs (`delete_club_hard` etc.) write to,
 * gated to platform admins only by Firestore rules. We call this from the
 * admin tokens drawer on every cleanup-flag mutation so the history of
 * "who pinned this mockup, when, and why" survives the next change.
 */
async function writeDataOperation(
  clubId: string,
  kind: string,
  payload: Record<string, unknown>,
): Promise<void> {
  const user = auth.currentUser
  await addDoc(collection(db, clubSubcollections.dataOperations(clubId)), {
    kind,
    actorUid: user?.uid ?? '',
    actorEmail: user?.email ?? '',
    at: serverTimestamp(),
    payload,
  })
}

/**
 * Admin-only repository. Lists every club (bypass the per-user membership
 * query), pings activity, and orchestrates the hard-delete Cloud Function.
 * Per-club preview tokens live in their own repository — see
 * `preview-tokens-repository.ts`.
 *
 * Reads rely on the `admin` bypass in Firestore rules — non-admin callers will
 * get a permission error.
 */
export class AdminRepository extends FirestoreRepository<typeof ClubSchema> {
  constructor() {
    super(ROOT_CLUBS, ClubSchema)
  }

  async listAllClubs(): Promise<Club[]> {
    const snap = await getDocs(this.collectionRef)
    const out: Club[] = []
    for (const d of snap.docs) {
      const parsed = this.parse(d.id, d.data())
      if (parsed) out.push(parsed)
    }
    return out
  }

  // Legacy `generatePreviewToken` / `revokePreviewToken` were removed when the
  // single-token-per-club model was replaced by the per-club `preview_tokens`
  // subcollection. See `features/admin/api/preview-tokens-repository.ts` and
  // docs/preview-mockup-and-trial-flow.md.

  /** Lightweight ping — write only `lastActivityAt` without touching
   * `updatedAt`. Intent: periodic heartbeat from any signed-in club member.
   * The caller is responsible for debouncing to ~1 write per 15 min. */
  async touchLastActivity(clubId: string): Promise<void> {
    await updateDoc(doc(db, ROOT_CLUBS, clubId), {
      lastActivityAt: serverTimestamp(),
    })
  }

  /** Admin-only manual override for the `isDemo` flag. The flag fans out
   * to 10+ CFs (booking triggers skip outbound comms, MRR aggregates
   * exclude it, daily summary push skipped, etc.) — `seed_club_demo_data`
   * and `convert_mockup_to_trial` are the supported "flip in context"
   * paths; this method is the bare-flag escape hatch when those don't fit.
   *
   * On `true`: stamps `demoCreatedAt: serverTimestamp()` so the cleanup CF
   * has a reference date, AND sets `noAutoCleanup: true` as a defensive
   * shield so a real club marked-as-demo can't get wiped by
   * `cleanup_mockup_clubs_daily`. Admin can flip `noAutoCleanup` back
   * separately if they actually want cleanup eligible.
   *
   * On `false`: clears `demoCreatedAt` (mirrors `convert_mockup_to_trial`'s
   * housekeeping, but skips the trial-start logic — manual-demote isn't
   * necessarily a trial-start).
   *
   * Audit row captures actor + before/after + whether the auto-shield
   * fired so the history is replayable. */
  async setIsDemo(
    clubId: string,
    value: boolean,
    previousValue: boolean | null = null,
  ): Promise<void> {
    const payload: Record<string, unknown> = {
      isDemo: value,
      updatedAt: serverTimestamp(),
    }
    if (value) {
      payload.demoCreatedAt = serverTimestamp()
      payload.noAutoCleanup = true
    } else {
      payload.demoCreatedAt = deleteField()
    }
    await updateDoc(doc(db, ROOT_CLUBS, clubId), payload)
    await writeDataOperation(clubId, 'set_is_demo', {
      from: previousValue,
      to: value,
      autoShieldNoAutoCleanup: value,
    })
  }

  /** Toggle the admin escape hatch for `cleanup_mockup_clubs_daily`. When
   * `true` the cleanup CF skips this club regardless of token state or
   * grace windows. Only meaningful while `status === 'mockup'`.
   *
   * Writes an audit row to `data_operations/` capturing actor + previous
   * value so the history survives subsequent changes. */
  async setNoAutoCleanup(
    clubId: string,
    value: boolean,
    previousValue: boolean | null = null,
  ): Promise<void> {
    await updateDoc(doc(db, ROOT_CLUBS, clubId), {
      noAutoCleanup: value,
      updatedAt: serverTimestamp(),
    })
    await writeDataOperation(clubId, 'set_no_auto_cleanup', {
      from: previousValue,
      to: value,
    })
  }

  /**
   * Admin-only: flip a single `platformFeatures.{key}` boolean on the club
   * doc. Firestore rules deny non-admin writes to this map, so the call
   * fails with a permission error if the caller isn't on
   * `platform_config/admins`. Writes an audit row capturing actor +
   * before/after so a future viewer can replay the toggle history.
   *
   * Used today by the AI narrative beta toggle in the admin tokens drawer.
   * As more `PlatformFeatures` flags land, the same method handles each —
   * one entry point, one audit shape, no per-flag duplication.
   */
  async setPlatformFeature<K extends keyof PlatformFeatures>(
    clubId: string,
    key: K,
    value: PlatformFeatures[K],
    previousValue: PlatformFeatures[K] | null = null,
  ): Promise<void> {
    await updateDoc(doc(db, ROOT_CLUBS, clubId), {
      [`platformFeatures.${String(key)}`]: value,
      updatedAt: serverTimestamp(),
    })
    await writeDataOperation(clubId, 'set_platform_feature', {
      key: String(key),
      from: previousValue,
      to: value,
    })
  }

  /** Admin-only: grant or revoke the permanent founder rate on `subscription.partnerClub`.
   * When value=true, agreementSignedAt is written alongside to satisfy the
   * `enforce_founder_club_cap` CF gate (which auto-reverts the flip when missing).
   * When value=false, the date is intentionally left in place as an audit trail.
   * Writes an audit row capturing actor + before/after for the toggle history. */
  async setPartnerClub(
    clubId: string,
    value: boolean,
    previousValue: boolean | null = null,
    agreementSignedAt?: Date | null,
  ): Promise<void> {
    const payload: Record<string, unknown> = {
      'subscription.partnerClub': value,
      updatedAt: serverTimestamp(),
    }
    if (value && agreementSignedAt) {
      // CF `enforce_founder_club_cap` rejects the flip without this field.
      payload['subscription.partnerAgreementSignedAt'] = agreementSignedAt
    }
    await updateDoc(doc(db, ROOT_CLUBS, clubId), payload)
    await writeDataOperation(clubId, 'set_partner_club', {
      from: previousValue,
      to: value,
      ...(agreementSignedAt ? { agreementSignedAt: agreementSignedAt.toISOString() } : {}),
    })
  }

  /**
   * Set (or clear) `subscription.graceEndDate` — the read-only → closure
   * deadline, Decision #16: never auto-computed, always set by hand by a
   * platform admin. `value: null` clears it back to indefinite read-only
   * (`deleteField()`, not just `null`, so `lifecycleMilestones()`'s
   * `!graceEndDate` check sees a genuinely absent field either way).
   * Firestore rules restrict owner writes on `subscription.*` to `plan`
   * alone, so — same as `setPartnerClub` — this is the only write path.
   */
  async setGraceEndDate(
    clubId: string,
    value: Date | null,
    previousValue: Date | null = null,
  ): Promise<void> {
    await updateDoc(doc(db, ROOT_CLUBS, clubId), {
      'subscription.graceEndDate': value ?? deleteField(),
      updatedAt: serverTimestamp(),
    })
    await writeDataOperation(clubId, 'set_grace_end_date', {
      from: previousValue ? previousValue.toISOString() : null,
      to: value ? value.toISOString() : null,
    })
  }

  /** Set `billingStatus` to `exempt` (ON) or `not_billable` (OFF) — the
   * platform-admin escape hatch that lets a club operate with full access
   * without going through Stripe. For uPadelMatch's own internal test clubs
   * on prod. NOT for customer comp / discount flows (use `setPartnerClub`).
   *
   * Lifecycle `status` is now ORTHOGONAL to billing and is left untouched —
   * a `mockup` stays `mockup` (+ "Exento" badge), a `trial` stays `trial`.
   * This is the whole point of the lifecycle⟂billing split: the exempt flag
   * no longer masks the lifecycle label. OFF returns billing to the
   * pre-billing default `not_billable` (the club keeps whatever lifecycle
   * stage it's in; the trial-end → Stripe flow applies if it's ever
   * converted to a real customer).
   *
   * Writes `billingStatus` + audit timestamp + audit actor in one update;
   * the audit row in `data_operations/` captures the before/after. Firestore
   * rules deny owners from touching `billingStatus`, so this is the only
   * write path.
   */
  async setBillingExempt(
    clubId: string,
    value: boolean,
    previousBillingStatus: BillingStatus | null = null,
    currentStatus: ClubStatus | null = null,
  ): Promise<void> {
    const user = auth.currentUser
    const next: BillingStatus = value ? 'exempt' : 'not_billable'
    // Granting Exento is the no-charge equivalent of a first cleared payment,
    // so it advances trial → active (same rule the Stripe webhook applies on
    // invoice.paid). Otherwise the comped club keeps a stale `trial` status
    // and the admin row shows the incoherent "Prueba caducada". Scoped to
    // trial; un-exempting never demotes. See `exemptLifecycleEffect`.
    const promotedStatus =
      currentStatus != null ? exemptLifecycleEffect(value, currentStatus) : null
    await updateDoc(doc(db, ROOT_CLUBS, clubId), {
      billingStatus: next,
      billingStatusSetAt: serverTimestamp(),
      billingStatusSetBy: user?.uid ?? user?.email ?? '',
      ...(promotedStatus ? { status: promotedStatus } : {}),
      updatedAt: serverTimestamp(),
    })
    await writeDataOperation(clubId, 'set_billing_exempt', {
      from: previousBillingStatus,
      to: next,
      ...(promotedStatus
        ? { statusFrom: currentStatus, statusTo: promotedStatus }
        : {}),
    })
  }

  /** Per-club override of the cleanup grace windows. `null` for either
   * field clears that override and falls back to the platform default in
   * `functions/mockup_cleanup.py`. Writes an audit row capturing the
   * before/after for each non-undefined key. */
  async setCleanupConfig(
    clubId: string,
    config: { ownerInviteGraceDays?: number | null; orphanGraceDays?: number | null },
    previousConfig: Club['cleanupConfig'] = null,
  ): Promise<void> {
    const payload: Record<string, unknown> = { updatedAt: serverTimestamp() }
    const cfg: Record<string, unknown> = {}
    if (config.ownerInviteGraceDays !== undefined) {
      cfg.ownerInviteGraceDays = config.ownerInviteGraceDays
    }
    if (config.orphanGraceDays !== undefined) {
      cfg.orphanGraceDays = config.orphanGraceDays
    }
    payload.cleanupConfig = cfg
    await updateDoc(doc(db, ROOT_CLUBS, clubId), payload)
    await writeDataOperation(clubId, 'set_cleanup_config', {
      from: previousConfig ?? null,
      to: cfg,
    })
  }

  /**
   * Recent rows from `clubs/{clubId}/data_operations`, newest first. Each
   * entry captures one admin mutation (toggle, configure, delete) with
   * actor + before/after payload. Powers the data-ops timeline in the
   * AdminClubActionsPanel — spec/16 BR-7. Read-only.
   */
  async listDataOperations(clubId: string, pageSize: number = 50): Promise<DataOperationEntry[]> {
    const colRef = collection(db, clubSubcollections.dataOperations(clubId))
    const q = query(colRef, orderBy('at', 'desc'), limit(pageSize))
    const snap = await getDocs(q)
    const out: DataOperationEntry[] = []
    for (const d of snap.docs) {
      const data = d.data()
      const at = data.at
      const atDate =
        at && typeof at === 'object' && 'toDate' in at && typeof at.toDate === 'function'
          ? (at.toDate() as Date)
          : at instanceof Date
            ? at
            : null
      out.push({
        id: d.id,
        kind: typeof data.kind === 'string' ? data.kind : 'unknown',
        actorUid: typeof data.actorUid === 'string' ? data.actorUid : '',
        actorEmail: typeof data.actorEmail === 'string' ? data.actorEmail : '',
        at: atDate,
        payload:
          data.payload && typeof data.payload === 'object'
            ? (data.payload as Record<string, unknown>)
            : {},
      })
    }
    return out
  }

  /**
   * Recent rows from the top-level `mockup_cleanup_log/` collection,
   * newest first. Powers the admin "Maquetas eliminadas" viewer. Returns
   * empty array when the cleanup CF hasn't deleted anything yet.
   */
  async listMockupCleanupLog(pageSize: number = 100): Promise<MockupCleanupLogEntry[]> {
    const colRef = collection(db, ROOT_MOCKUP_CLEANUP_LOG)
    const q = query(colRef, orderBy('deletedAt', 'desc'), limit(pageSize))
    const snap = await getDocs(q)
    const out: MockupCleanupLogEntry[] = []
    for (const d of snap.docs) {
      const parsed = MockupCleanupLogEntrySchema.safeParse({ id: d.id, ...d.data() })
      if (parsed.success) out.push(parsed.data)
    }
    return out
  }

  /**
   * Permanently delete a club and every subcollection beneath it via the
   * `delete_club_hard` Cloud Function. The function re-checks platform-admin
   * auth and re-checks the typed-name match before deleting — the client
   * cannot bypass either. Subcollections aren't reachable from the client
   * SDK in a single transaction, so this MUST go through the function.
   */
  async hardDeleteClub(clubId: string, confirmName: string): Promise<HardDeleteResult> {
    const fn = httpsCallable<HardDeleteInput, HardDeleteResult>(
      functions,
      'delete_club_hard',
    )
    const res = await fn({ clubId, confirmName })
    return res.data
  }

  /**
   * Wipe seeded data from a demo club via the `reset_demo_club` CF.
   * Deletes everything created by `seed_club_demo_data` (bookings,
   * sales, activity_log, club_feedback — anything stamped
   * `createdByUid: "seed-script"`) but keeps the club shell + staff +
   * courts + catalogue + customer roster. Backend guards: only clubs
   * with `isDemo: true`, only the owner or a platform admin.
   *
   * Use case: after a model contract change (e.g. tax-inclusive flip),
   * old seeded bookings still carry the old shape — wipe + regenerate
   * to get a fresh dataset matching the current code.
   */
  async resetDemoClub(clubId: string): Promise<ResetDemoClubResult> {
    const fn = httpsCallable<ResetDemoClubInput, ResetDemoClubResult>(
      functions,
      'reset_demo_club',
    )
    const res = await fn({ clubId })
    return res.data
  }

  /**
   * Regenerate seed data on a demo club via `seed_club_demo_data`. The
   * CF is the same one called by onboarding when `withDemoData: true`;
   * here we call it again post-onboarding to refresh the dataset.
   * Best run after `resetDemoClub()` so the new data lands in a clean
   * collection — otherwise you'll get duplicates.
   */
  async seedDemoData(clubId: string): Promise<SeedDemoDataResult> {
    const fn = httpsCallable<SeedDemoDataInput, SeedDemoDataResult>(
      functions,
      'seed_club_demo_data',
    )
    const res = await fn({ clubId })
    return res.data
  }

  /**
   * Live-subscribe to the `deletionProgress` field on `clubs/{clubId}`.
   * The destructive CFs (`delete_club_hard` + `convert_mockup_to_trial`'s
   * seed-wipe phase) write `{ phase, done, total, updatedAt }` every ~50
   * docs via BulkWriter so the calling UI can render a progress bar
   * instead of a frozen spinner during long wipes. `null` means no
   * progress field present (idle / done / club doc deleted).
   *
   * Returns the unsubscribe function — caller is responsible for cleanup.
   */
  onDeletionProgress(
    clubId: string,
    cb: (progress: DeletionProgress | null) => void,
  ): () => void {
    return onSnapshot(
      doc(db, ROOT_CLUBS, clubId),
      (snap) => {
        const data = snap.data()
        const p = data?.deletionProgress as
          | { phase?: unknown; done?: unknown; total?: unknown }
          | undefined
        if (p && typeof p === 'object') {
          cb({
            phase: typeof p.phase === 'string' ? p.phase : '',
            done: typeof p.done === 'number' ? p.done : 0,
            total: typeof p.total === 'number' ? p.total : 0,
          })
        } else {
          cb(null)
        }
      },
      () => {
        // Listener errors are non-fatal for the caller — surface as no-progress.
        cb(null)
      },
    )
  }
}

export interface DeletionProgress {
  phase: string
  done: number
  total: number
}

export interface DataOperationEntry {
  id: string
  kind: string
  actorUid: string
  actorEmail: string
  at: Date | null
  payload: Record<string, unknown>
}

interface HardDeleteInput {
  clubId: string
  confirmName: string
}

interface ResetDemoClubInput {
  clubId: string
  keepPlayers?: boolean
}

export interface ResetDemoClubResult {
  ok: boolean
  deleted?: Record<string, number>
}

interface SeedDemoDataInput {
  clubId: string
}

export interface SeedDemoDataResult {
  ok: boolean
  bookingsCreated?: number
  playersCreated?: number
  salesCreated?: number
}

export interface HardDeleteResult {
  deletedClubId: string
  deletedClubName: string
  deletedSubcollections: string[]
}

export const adminRepository = new AdminRepository()
